import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import Toolbar from "react-multi-date-picker/plugins/toolbar";
import axios from "axios";

const FIELD_LABELS = {
  first_name: "نام",
  last_name: "نام خانوادگی",
  dob: "تاریخ تولد",
  gender: "جنسیت",
  mobile: "شماره موبایل",
  email: "ایمیل",
  address: "آدرس",
  education: "آخرین مدرک تحصیلی",
  university: "دانشگاه",
  field: "رشته تحصیلی",
  last_job_title: "عنوان آخرین شغل",
  last_workplace: "محل کار",
  last_job_duration: "مدت زمان اشتغال",
  english_reading: "انگلیسی - خواندن",
  english_writing: "انگلیسی - نوشتن",
  english_speaking: "انگلیسی - مکالمه",
  arabic_reading: "عربی - خواندن",
  arabic_writing: "عربی - نوشتن",
  arabic_speaking: "عربی - مکالمه",
  desired_job_id: "عنوان شغلی مورد درخواست",
  employment_type: "نوع استخدام",
  preferred_shift: "شیفت کاری مورد نظر",
  salary_expectation: "انتظار حقوقی",
  photo: "عکس شخصی",
  skills: "مهارت‌های کلیدی",
};

const INITIAL_FORM = {
  first_name: "",
  last_name: "",
  dob: null,
  gender: "",
  mobile: "",
  email: "",
  address: "",
  education: "",
  university: "",
  field: "",
  last_job_title: "",
  last_workplace: "",
  last_job_duration: "",
  english_reading: "",
  english_writing: "",
  english_speaking: "",
  arabic_reading: "",
  arabic_writing: "",
  arabic_speaking: "",
  desired_job_id: "",
  employment_type: "",
  preferred_shift: "",
  salary_expectation: "",
};

const LANGUAGE_LEVELS = (
  <>
    <option value="">انتخاب</option>
    <option value="کم">کم</option>
    <option value="متوسط">متوسط</option>
    <option value="زیاد">زیاد</option>
  </>
);

const JobApplication = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [skills, setSkills] = useState([""]);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoName, setPhotoName] = useState("");
  const [resume, setResume] = useState(null);
  const [resumeName, setResumeName] = useState("");
  const [jobTitles, setJobTitles] = useState([]);
  const [result, setResult] = useState({ message: "", type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const feedbackRef = useRef(null);

  useEffect(() => {
    loadJobTitles();
  }, []);

  useEffect(() => {
    if (result.message && result.type !== "loading" && feedbackRef.current) {
      feedbackRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [result]);

  const loadJobTitles = async () => {
    try {
      const response = await axios.get("/api/job-titles");
      setJobTitles(response.data);
    } catch (error) {
      console.error("Error loading job titles:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, dob: date }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    setPhotoName(file?.name || "");
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    setResumeName(file?.name || "");
    setResume(file || null);
  };

  const handleSkillChange = (index, value) => {
    const next = [...skills];
    next[index] = value;
    setSkills(next);
  };

  const addSkill = () => setSkills([...skills, ""]);

  const removeSkill = (index) => {
    if (skills.length > 1) {
      setSkills(skills.filter((_, i) => i !== index));
    }
  };

  const validateForm = () => {
    const requiredFields = [
      "first_name",
      "last_name",
      "gender",
      "mobile",
      "address",
      "education",
      "field",
      "last_job_title",
      "last_workplace",
      "last_job_duration",
      "english_reading",
      "english_writing",
      "english_speaking",
      "arabic_reading",
      "arabic_writing",
      "arabic_speaking",
      "desired_job_id",
      "employment_type",
      "preferred_shift",
      "salary_expectation",
    ];

    for (const field of requiredFields) {
      if (!formData[field] || formData[field].toString().trim() === "") {
        return `لطفاً فیلد «${FIELD_LABELS[field] || field}» را پر کنید`;
      }
    }

    if (!formData.dob) {
      return `لطفاً فیلد «${FIELD_LABELS.dob}» را پر کنید`;
    }

    if (!/^09\d{9}$/.test(formData.mobile.trim())) {
      return "شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود";
    }

    if (!photo) {
      return `لطفاً «${FIELD_LABELS.photo}» را آپلود کنید`;
    }

    if (skills.filter((s) => s.trim() !== "").length === 0) {
      return `حداقل یک ${FIELD_LABELS.skills} وارد کنید`;
    }

    return null;
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setSkills([""]);
    setPhoto(null);
    setPhotoPreview(null);
    setPhotoName("");
    setResume(null);
    setResumeName("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setResult({ message: validationError, type: "error" });
      return;
    }

    setIsSubmitting(true);
    setResult({ message: "در حال ارسال...", type: "loading" });

    try {
      const submitData = new FormData();
      submitData.append(
        "full_name",
        `${formData.first_name} ${formData.last_name}`
      );

      if (formData.dob) {
        submitData.append("dob", new Date(formData.dob).toString());
      } else {
        submitData.append("dob", "");
      }

      Object.keys(formData).forEach((key) => {
        if (key !== "first_name" && key !== "last_name" && key !== "dob") {
          submitData.append(key, formData[key]);
        }
      });

      submitData.append(
        "skills",
        JSON.stringify(skills.filter((skill) => skill.trim() !== ""))
      );

      if (photo) submitData.append("photo", photo);
      if (resume) submitData.append("resume", resume);

      const response = await axios.post("/api/apply", submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult({
          message: "درخواست شما با موفقیت ثبت شد!",
          type: "success",
        });
        resetForm();
      }
    } catch (error) {
      setResult({
        message: error.response?.data?.error || "خطا در ارسال درخواست",
        type: "error",
      });
    }

    setIsSubmitting(false);
  };

  return (
    <div className="container application-page">
      <div className="header application-header">
        <div>
          <h1>فرم استخدام</h1>
          <p className="header-subtitle">
            لطفاً اطلاعات خود را کامل و دقیق وارد کنید
          </p>
        </div>
        <a href="/admin" className="admin-link-quiet">
          ورود مدیران
        </a>
      </div>

      <div ref={feedbackRef}>
        {result.message && (
          <div className={`result-message ${result.type}`} role="alert">
            {result.message}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} encType="multipart/form-data" noValidate>
        <section className="form-section">
          <div className="form-section-header">
            <span className="form-section-step">۱</span>
            <div>
              <h2>اطلاعات شخصی</h2>
              <p>مشخصات فردی و راه‌های ارتباطی</p>
            </div>
          </div>

          <div className="photo-upload">
            <label>عکس شخصی *</label>
            <div className="form-row photo-upload-row">
              <div className="form-col">
                <label className="custom-file" htmlFor="photo-input">
                  انتخاب عکس
                  <input
                    type="file"
                    id="photo-input"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </label>
                <span className="file-name">
                  {photoName || "فایلی انتخاب نشده است"}
                </span>
              </div>
              <div className="form-col">
                {photoPreview && (
                  <img
                    src={photoPreview}
                    alt="پیش‌نمایش"
                    className="photo-preview"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="first_name">نام *</label>
              <input
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                autoComplete="given-name"
              />
            </div>
            <div className="form-col">
              <label htmlFor="last_name">نام خانوادگی *</label>
              <input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col half">
              <label>تاریخ تولد *</label>
              <DatePicker
                plugins={[<Toolbar key="date-toolbar" position="bottom" />]}
                buttons
                placeholder="تاریخ تولد را انتخاب کنید"
                onChange={handleDateChange}
                value={formData.dob}
                calendar={persian}
                locale={persian_fa}
                calendarPosition="bottom-right"
                zIndex={66}
                inputClass="form-datepicker-input"
                format="YYYY/MM/DD"
              />
            </div>
            <div className="form-col half">
              <label htmlFor="gender">جنسیت *</label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
              >
                <option value="">انتخاب کنید</option>
                <option value="مرد">مرد</option>
                <option value="زن">زن</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="mobile">شماره موبایل *</label>
              <input
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                inputMode="numeric"
                placeholder="09123456789"
                autoComplete="tel"
              />
            </div>
            <div className="form-col">
              <label htmlFor="email">
                ایمیل <span className="optional-tag">اختیاری</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="example@email.com"
                autoComplete="email"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="address">آدرس *</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows={3}
                placeholder="آدرس کامل محل سکونت"
              />
            </div>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section-header">
            <span className="form-section-step">۲</span>
            <div>
              <h2>تحصیلات</h2>
              <p>آخرین مدرک و رشته تحصیلی</p>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="education">آخرین مدرک تحصیلی *</label>
              <select
                id="education"
                name="education"
                value={formData.education}
                onChange={handleInputChange}
              >
                <option value="">انتخاب کنید</option>
                <option value="دیپلم">دیپلم</option>
                <option value="کاردانی">کاردانی</option>
                <option value="لیسانس">لیسانس</option>
                <option value="فوق لیسانس">فوق لیسانس</option>
                <option value="دکتری">دکتری</option>
              </select>
            </div>
            <div className="form-col">
              <label htmlFor="field">رشته تحصیلی *</label>
              <input
                id="field"
                name="field"
                value={formData.field}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="university">
                دانشگاه محل تحصیل <span className="optional-tag">اختیاری</span>
              </label>
              <input
                id="university"
                name="university"
                value={formData.university}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section-header">
            <span className="form-section-step">۳</span>
            <div>
              <h2>سوابق شغلی و مهارت‌ها</h2>
              <p>آخرین تجربه کاری و مهارت‌های کلیدی</p>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="last_job_title">عنوان آخرین شغل *</label>
              <input
                id="last_job_title"
                name="last_job_title"
                value={formData.last_job_title}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-col">
              <label htmlFor="last_workplace">محل کار *</label>
              <input
                id="last_workplace"
                name="last_workplace"
                value={formData.last_workplace}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="last_job_duration">مدت زمان اشتغال *</label>
              <input
                id="last_job_duration"
                name="last_job_duration"
                value={formData.last_job_duration}
                onChange={handleInputChange}
                placeholder="مثال: ۲ سال"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col full">
              <label>مهارت‌های کلیدی *</label>
              <div className="skills-container">
                {skills.map((skill, index) => (
                  <div key={index} className="skill-item">
                    <input
                      type="text"
                      value={skill}
                      onChange={(e) => handleSkillChange(index, e.target.value)}
                      placeholder="مهارت را وارد کنید"
                      aria-label={`مهارت ${index + 1}`}
                    />
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeSkill(index)}
                      disabled={skills.length === 1}
                    >
                      حذف
                    </button>
                  </div>
                ))}
                <button type="button" className="btn-add" onClick={addSkill}>
                  افزودن مهارت
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section-header">
            <span className="form-section-step">۴</span>
            <div>
              <h2>زبان‌های خارجی</h2>
              <p>سطح مهارت در انگلیسی و عربی</p>
            </div>
          </div>

          <div className="language-table-wrap">
            <table className="language-table">
              <thead>
                <tr>
                  <th>زبان</th>
                  <th>خواندن</th>
                  <th>نوشتن</th>
                  <th>مکالمه</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="lang-name">انگلیسی</td>
                  <td>
                    <select
                      name="english_reading"
                      value={formData.english_reading}
                      onChange={handleInputChange}
                      aria-label="انگلیسی خواندن"
                    >
                      {LANGUAGE_LEVELS}
                    </select>
                  </td>
                  <td>
                    <select
                      name="english_writing"
                      value={formData.english_writing}
                      onChange={handleInputChange}
                      aria-label="انگلیسی نوشتن"
                    >
                      {LANGUAGE_LEVELS}
                    </select>
                  </td>
                  <td>
                    <select
                      name="english_speaking"
                      value={formData.english_speaking}
                      onChange={handleInputChange}
                      aria-label="انگلیسی مکالمه"
                    >
                      {LANGUAGE_LEVELS}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td className="lang-name">عربی</td>
                  <td>
                    <select
                      name="arabic_reading"
                      value={formData.arabic_reading}
                      onChange={handleInputChange}
                      aria-label="عربی خواندن"
                    >
                      {LANGUAGE_LEVELS}
                    </select>
                  </td>
                  <td>
                    <select
                      name="arabic_writing"
                      value={formData.arabic_writing}
                      onChange={handleInputChange}
                      aria-label="عربی نوشتن"
                    >
                      {LANGUAGE_LEVELS}
                    </select>
                  </td>
                  <td>
                    <select
                      name="arabic_speaking"
                      value={formData.arabic_speaking}
                      onChange={handleInputChange}
                      aria-label="عربی مکالمه"
                    >
                      {LANGUAGE_LEVELS}
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section-header">
            <span className="form-section-step">۵</span>
            <div>
              <h2>درخواست شغلی</h2>
              <p>موقعیت مورد نظر و فایل رزومه</p>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="desired_job_id">عنوان شغلی مورد درخواست *</label>
              <select
                id="desired_job_id"
                name="desired_job_id"
                value={formData.desired_job_id}
                onChange={handleInputChange}
              >
                <option value="">انتخاب کنید...</option>
                {jobTitles.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-col">
              <label htmlFor="employment_type">نوع استخدام *</label>
              <select
                id="employment_type"
                name="employment_type"
                value={formData.employment_type}
                onChange={handleInputChange}
              >
                <option value="">انتخاب کنید</option>
                <option value="تمام وقت">تمام وقت</option>
                <option value="پاره وقت">پاره وقت</option>
                <option value="پروژه‌ای">پروژه‌ای</option>
                <option value="کارآموزی">کارآموزی</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="preferred_shift">شیفت کاری مورد نظر *</label>
              <select
                id="preferred_shift"
                name="preferred_shift"
                value={formData.preferred_shift}
                onChange={handleInputChange}
              >
                <option value="">انتخاب کنید</option>
                <option value="شیفت صبح (۹ الی ۱۷)">شیفت صبح (۹ الی ۱۷)</option>
                <option value="شیفت عصر (۱۷ الی ۱)">شیفت عصر (۱۷ الی ۱)</option>
                <option value="فرقی نمی‌کند">فرقی نمی‌کند</option>
              </select>
            </div>
            <div className="form-col">
              <label htmlFor="salary_expectation">انتظار حقوقی (تومان) *</label>
              <input
                id="salary_expectation"
                name="salary_expectation"
                value={formData.salary_expectation}
                onChange={handleInputChange}
                inputMode="numeric"
                placeholder="مثال: 15000000"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col">
              <label htmlFor="resume-input">
                فایل رزومه <span className="optional-tag">اختیاری</span>
              </label>
              <p className="field-hint">فرمت‌های مجاز: PDF, DOC, DOCX</p>
              <label className="custom-file" htmlFor="resume-input">
                انتخاب فایل
                <input
                  type="file"
                  id="resume-input"
                  accept=".pdf,.doc,.docx"
                  onChange={handleResumeChange}
                />
              </label>
              <span className="file-name">
                {resumeName || "فایلی انتخاب نشده است"}
              </span>
            </div>
          </div>
        </section>

        <div className="form-submit-bar">
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? "در حال ارسال..." : "ارسال درخواست"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobApplication;
